import { useState, type FormEvent } from 'react'
import { KeyRound, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { ApiError } from '../lib/api'

interface Props {
  forced?: boolean
  onClose?: () => void
}

export default function ChangePasswordModal({ forced = false, onClose }: Props) {
  const { t } = useTranslation('common')
  const { changePassword } = useAuth()
  const [currentPassword, setCurrent] = useState('')
  const [newPassword, setNew] = useState('')
  const [confirmPassword, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await changePassword(currentPassword, newPassword, confirmPassword)
      onClose?.()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as
          | { error?: string; issues?: { fieldErrors?: Record<string, string[]> } }
          | undefined
        const fieldErr = body?.issues?.fieldErrors
        const firstFieldErr = fieldErr ? Object.values(fieldErr).flat().find(Boolean) : undefined
        setError(firstFieldErr ?? body?.error ?? t('changePassword.errors.changeFailed'))
      } else {
        setError(t('changePassword.errors.network'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-3 py-4 md:px-4">
      <div className="w-full max-w-md surface-deep p-5 md:p-7 animate-slideUp max-h-[calc(100vh-2rem)] overflow-y-auto">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl grid place-items-center bg-toga-500/20 text-toga-700 dark:text-toga-300">
            <KeyRound size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold t-primary">
              {forced ? t('changePassword.titleForced') : t('changePassword.title')}
            </h2>
            <p className="text-xs t-muted">
              {forced
                ? t('changePassword.descriptionForced')
                : t('changePassword.description')}
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('changePassword.currentPassword')}</label>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(e) => setCurrent(e.target.value)}
              className="w-full px-3.5 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60"
            />
          </div>
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('changePassword.newPassword')}</label>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              value={newPassword}
              onChange={(e) => setNew(e.target.value)}
              className="w-full px-3.5 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60"
            />
          </div>
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('changePassword.confirmPassword')}</label>
            <input
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full px-3.5 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60"
            />
          </div>

          {error && (
            <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            {!forced && onClose && (
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg text-sm bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)]"
              >
                {t('actions.cancel')}
              </button>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga disabled:opacity-50 transition-all flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {t('changePassword.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
